import { Action, State, type StateContext } from '@ngxs/store';
import { produce } from 'immer';
import { Injectable } from '@angular/core';
import type { TableStateModel } from './table-state.model';
import { TABLE_STATE_TOKEN } from './table-state.token';
import { ChangePaginationAction } from './action/change-pagination.action';
import { SortAction } from './action/sort.action';
import { ChangeColumnVisibilityAction } from './action/change-column-visibility.action';
import { isNil } from '@pct-ts/typeguard';
import { ChangeFilterAction } from './action/change-filter.action';

type TableStateContext<T> = StateContext<TableStateModel<T>>;

@State<TableStateModel<unknown>>({
  name: TABLE_STATE_TOKEN,
  defaults: produce({} as TableStateModel<unknown>, () => ({}) satisfies TableStateModel<unknown>),
})
@Injectable()
export class TableState {
  @Action(ChangePaginationAction)
  changePagination<T>(
    { setState }: TableStateContext<T>,
    { pagination }: ChangePaginationAction,
  ): void {
    setState(
      produce(draft => {
        if (isNil(draft[pagination.tableId])) {
          draft[pagination.tableId] = {};
        }
        draft[pagination.tableId].pagination = { page: pagination.page, size: pagination.size };
      }),
    );
  }

  @Action(SortAction)
  sort<T>({ setState }: TableStateContext<T>, { sort }: SortAction<unknown>): void {
    setState(
      produce(draft => {
        if (isNil(draft[sort.tableId])) {
          draft[sort.tableId] = {};
        }
        draft[sort.tableId].sort = undefined;
        if (!isNil(sort.field) && !isNil(sort.order)) {
          draft[sort.tableId].sort = { field: sort.field, order: sort.order };
        }
      }),
    );
  }

  @Action(ChangeFilterAction)
  changeFilter({ setState }: TableStateContext<unknown>, { filters }: ChangeFilterAction): void {
    const { tableId, value } = filters;
    setState(
      produce(draft => {
        if (isNil(draft[tableId])) {
          draft[tableId] = {};
        }
        draft[tableId].filters = value;
        draft[tableId].pagination = { size: draft[tableId].pagination?.size ?? 10, page: 1 };
      }),
    );
  }

  @Action(ChangeColumnVisibilityAction)
  ChangeColumnVisibility<T>(
    { setState }: TableStateContext<T>,
    { columnVisibility }: ChangeColumnVisibilityAction,
  ): void {
    setState(
      produce(draft => {
        if (isNil(draft[columnVisibility.tableId])) {
          draft[columnVisibility.tableId] = {};
        }
        draft[columnVisibility.tableId].displayedColumns = columnVisibility.displayedColumns;
      }),
    );
  }
}
